// Pieces of the staff Home ("Business overview") with no JSX: ranges, the
// greeting, deltas, rates and the money cards. In a `.js` file so
// `react-refresh/only-export-components` stays quiet, same as
// components/staff-leads/model.js.
//
// Shape of what /admin/home/overview returns: HomeOverviewOut in the
// backend's leads/home_api.py, computed by leads/home_overview.py. Two scales
// to keep apart: every `delta_pct` is already a percentage (12.3 = +12.3%),
// every rate (`sold_rate.value`, `sale_rate`, `win_rate`, `error_rate`) is a
// fraction (0.123 = 12.3%).
import { money } from '../financial/formatting'
import { FALLBACK_TIMEZONE } from '../staff-leads/model'

export const ENDPOINT = '/admin/home/overview'
export const DEFAULT_RANGE = 'today'

// HomeRange in leads/home_overview.py, less `custom`, which RangeSelect's
// Custom button sends with date_from / date_to. Anything else is a 422.
export const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'mtd', label: 'MTD' },
]

// Every preset moves (they all end now), so every range refreshes - a
// custom one in the past just answers the same.
export const AUTO_REFRESH_MS = 5 * 60_000

// The only name a hidden buyer ever has on this screen. The backend folds
// every hidden buyer into one row and one balance under it, with no id.
export const OTHER_BUYER_LABEL = 'Other buyer'

// At or above this share of failed pings a buyer's error rate reads red.
export const HIGH_ERROR_RATE = 0.1

const n = (v) => Number(v || 0)

// ── header ──────────────────────────────────────────────────────────────

// "Good morning" by the New York clock, whatever the browser's.
export function greeting(date = new Date(), timeZone = FALLBACK_TIMEZONE) {
  const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hourCycle: 'h23' }).format(date))
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

export function firstName(user) {
  const first = String(user?.first_name || '').trim().split(/\s+/)[0]
  return first || ''
}

// ── numbers ─────────────────────────────────────────────────────────────

// A delta the backend could not compute (no baseline) is null and shows
// nothing - never "0%", which would claim a comparison that did not happen.
export function fmtDelta(deltaPct) {
  if (deltaPct === null || deltaPct === undefined || Number.isNaN(Number(deltaPct))) return null
  const v = Number(deltaPct)
  const abs = Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 1 })
  if (v > 0) return { text: `↑ ${abs}%`, tone: 'up' }
  if (v < 0) return { text: `↓ ${abs}%`, tone: 'down' }
  return { text: '0%', tone: 'flat' }
}

export const DELTA_COLOR = { up: 'var(--green)', down: 'var(--red)', flat: 'var(--text-muted)' }

// A fraction as "42.5%"; a rate over nothing is null and reads "—".
export function rate(fraction) {
  if (fraction === null || fraction === undefined) return '—'
  return `${(Number(fraction) * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

export const isHighErrorRate = (r) => r !== null && r !== undefined && Number(r) >= HIGH_ERROR_RATE

// Our share as a share of revenue: "62.5% of revenue". Nothing when there
// was no revenue.
export function shareOfRevenue(part, revenue) {
  return n(revenue) > 0 ? rate(n(part) / n(revenue)) : null
}

// ── alerts ──────────────────────────────────────────────────────────────

// home_overview HIGH / WATCH / INFO. An unknown level reads as Info.
export const LEVEL_META = {
  high: { label: 'High', fg: 'var(--red)', bg: 'var(--red-bg)' },
  watch: { label: 'Watch', fg: 'var(--amber)', bg: 'var(--amber-bg)' },
  info: { label: 'Info', fg: 'var(--accent)', bg: 'var(--accent-soft)' },
}
export const levelMeta = (level) => LEVEL_META[level] || LEVEL_META.info

// ── lead flow ───────────────────────────────────────────────────────────

// The bars, each as a share of received. sold + accepted_not_sold +
// rejected_by_routing + still_routing add up to received (the backend
// checks it); accepted_by_routing is the first two together. Intake
// rejections are not leads, so they stay out of the bars and get their own
// line.
//
// rejected_by_routing and still_routing are newer than the rest of `flow`:
// from a backend that does not send them, their bars are simply left out.
// still_routing is also left out at zero - most of the time nothing is.
export function flowRows(flow) {
  const received = n(flow?.received)
  const share = (v) => (received > 0 ? (n(v) / received) * 100 : 0)
  const row = (id, label, color) => ({ id, label, value: n(flow?.[id]), pct: share(flow?.[id]), color })
  const sent = (id) => typeof flow?.[id] === 'number'
  return [
    { id: 'received', label: 'Received', value: received, pct: received > 0 ? 100 : 0, color: 'var(--accent)' },
    row('accepted_by_routing', 'Accepted by routing', 'var(--blue)'),
    row('sold', 'Sold', 'var(--green)'),
    row('accepted_not_sold', 'Accepted not sold', 'var(--amber)'),
    sent('rejected_by_routing') && row('rejected_by_routing', 'Rejected by routing', 'var(--red)'),
    sent('still_routing') && flow.still_routing > 0 && row('still_routing', 'Still routing', 'var(--text-muted)'),
  ].filter(Boolean)
}

// ── money ───────────────────────────────────────────────────────────────

const present = (v) => v !== null && v !== undefined

// The four cards of "Money in and out", straight from `money`: each figure
// is Financial's own, and nothing here adds two of them together. In
// particular the Partners Edge legacy balance is already inside what we owe
// partners, so it is shown beside it and never summed. A block the backend
// sends as null is a card with no figure; a subtitle whose value is null
// (invoice age, next payment date - neither exists yet) is left out rather
// than shown as "null", "N/A" or an estimate.
export function moneyCards(m = {}) {
  const byBuyers = m?.owed_by_buyers
  const toPartners = m?.owed_to_partners
  const legacy = m?.partners_edge_legacy_balance
  const mtd = m?.mtd_our_share

  const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

  return [
    {
      id: 'owed_by_buyers',
      title: 'Owed to us by buyers',
      value: byBuyers ? money(byBuyers.amount) : null,
      lines: byBuyers
        ? [
            n(byBuyers.buyers_with_balance) > 0 ? `${plural(n(byBuyers.buyers_with_balance), 'buyer')} with a balance` : null,
            present(byBuyers.oldest_invoice_age_days) ? `Oldest invoice ${plural(n(byBuyers.oldest_invoice_age_days), 'day')} old` : null,
            present(byBuyers.other_buyer_outstanding) ? `Excludes ${OTHER_BUYER_LABEL}: ${money(byBuyers.other_buyer_outstanding)}` : null,
          ].filter(Boolean)
        : [],
    },
    {
      id: 'owed_to_partners',
      title: 'We owe partners',
      value: toPartners ? money(toPartners.amount) : null,
      lines: toPartners && present(toPartners.next_payment_date) ? [`Next payment ${toPartners.next_payment_date}`] : [],
    },
    {
      id: 'partners_edge_legacy_balance',
      title: 'Partners Edge July balance',
      value: legacy ? money(legacy.amount) : null,
      lines: legacy ? ['Included in what we owe partners'] : [],
    },
    {
      id: 'mtd_our_share',
      title: 'Month to date our share',
      value: mtd ? money(mtd.value) : null,
      deltaPct: mtd ? mtd.delta_pct : null,
      comparison: 'vs same days last month',
      lines: mtd ? [`${money(mtd.previous_month_same_day)} same days last month`] : [],
    },
  ]
}

export const HOME_RESPONSIVE_CSS = `
/* No backticks in these comments: this lives inside a JS template literal. */
.hm-kpis { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.hm-split { display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.hm-money { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.hm-card-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.hm-link { font-size: 12px; font-weight: 600; color: var(--accent); text-decoration: none; white-space: nowrap; }
.hm-link:hover { text-decoration: underline; }
.hm-link:focus-visible, .hm-day:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.hm-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.hm-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hm-table th { text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); padding: 8px 10px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.hm-table td { padding: 9px 10px; border-bottom: 1px solid var(--border); color: var(--text-primary); }
.hm-table tr:last-child td { border-bottom: none; }
.hm-table .hm-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.hm-day-bars { display: grid; grid-template-columns: repeat(14, minmax(0, 1fr)); gap: 6px; align-items: end; height: 180px; position: relative; }
.hm-day-labels { display: grid; grid-template-columns: repeat(14, minmax(0, 1fr)); gap: 6px; border-top: 1px solid var(--border-strong); padding-top: 4px; }
.hm-skeleton { background: var(--bg-raised); border-radius: 6px; animation: hm-pulse 1.2s ease-in-out infinite; }
@keyframes hm-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: reduce) { .hm-skeleton { animation: none; } }
@media (max-width: 1100px) {
  .hm-kpis, .hm-money { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .hm-split { grid-template-columns: 1fr !important; }
}
@media (max-width: 768px) {
  .hm-header { flex-direction: column !important; align-items: stretch !important; }
  .hm-day-bars, .hm-day-labels { gap: 3px; }
  .hm-day-label-odd { visibility: hidden; }
}
@media (max-width: 480px) {
  .hm-kpis, .hm-money { grid-template-columns: 1fr !important; }
}
`
